import { api } from "@/lib/apiClient";

export type ChartAccountLevel = 1 | 2 | 3;
export type ChartBalanceSide = "Dr" | "Cr";

export type ChartOfAccountNode = {
  id: string;
  accountId: number | null;
  level: ChartAccountLevel;
  name: string;
  code: string;
  accountClass: string;
  accountGroup: string | null;
  balance: number;
  balanceSide: ChartBalanceSide;
  children: ChartOfAccountNode[];
};

type ApiChartOfAccountNode = {
  id: string;
  account_id?: number | null;
  level: number;
  name: string;
  code: string;
  account_class: string;
  account_group: string | null;
  balance: number | string;
  balance_side: string;
  children?: ApiChartOfAccountNode[];
};

function mapNode(raw: ApiChartOfAccountNode): ChartOfAccountNode {
  const level = raw.level === 2 || raw.level === 3 ? raw.level : 1;
  const side = raw.balance_side === "Cr" ? "Cr" : "Dr";

  return {
    id: raw.id,
    accountId: raw.account_id != null ? Number(raw.account_id) : null,
    level,
    name: raw.name,
    code: raw.code ?? "",
    accountClass: raw.account_class ?? "N/A",
    accountGroup: raw.account_group,
    balance: Number(raw.balance ?? 0),
    balanceSide: side,
    children: (raw.children ?? []).map(mapNode),
  };
}

export async function fetchChartOfAccountsTree(): Promise<ChartOfAccountNode[]> {
  const response = await api.get<{ data: ApiChartOfAccountNode[] }>("/api/v1/chart-of-accounts");
  return (response.data.data ?? []).map(mapNode);
}

export function collectLeafAccountIds(node: ChartOfAccountNode): number[] {
  if (node.accountId != null && node.accountId > 0) {
    return [node.accountId];
  }
  return node.children.flatMap(collectLeafAccountIds);
}

export type ChartLedgerLine = {
  id: number;
  date: string | null;
  voucher: string | null;
  voucherType: string | null;
  accountId: number;
  accountName: string;
  accountCode: string;
  particulars: string;
  fromTo: string | null;
  reference: string | null;
  in: number;
  out: number;
  balance: number;
  balanceSide: ChartBalanceSide;
};

export type ChartLedger = {
  accounts: { id: number; name: string; code: string }[];
  lines: ChartLedgerLine[];
  totals: { in: number; out: number; balance: number; balanceSide: ChartBalanceSide };
};

type ApiChartLedger = {
  accounts?: { id: number; name: string; code?: string }[];
  lines?: {
    id: number;
    date?: string | null;
    voucher?: string | null;
    voucher_type?: string | null;
    account_id?: number;
    account_name?: string;
    account_code?: string;
    particulars?: string;
    from_to?: string | null;
    reference?: string | null;
    in?: number | string;
    out?: number | string;
    balance?: number | string;
    balance_side?: string;
  }[];
  totals?: { in?: number | string; out?: number | string; balance?: number | string; balance_side?: string };
};

export async function fetchChartOfAccountLedger(accountIds: number[]): Promise<ChartLedger> {
  const response = await api.get<{ data: ApiChartLedger }>("/api/v1/chart-of-accounts/ledger", {
    params: { account_ids: accountIds.join(",") },
  });
  const raw = response.data.data ?? {};
  return {
    accounts: (raw.accounts ?? []).map((a) => ({
      id: Number(a.id),
      name: a.name,
      code: a.code ?? "",
    })),
    lines: (raw.lines ?? []).map((line) => ({
      id: Number(line.id),
      date: line.date ?? null,
      voucher: line.voucher ?? null,
      voucherType: line.voucher_type ?? null,
      accountId: Number(line.account_id ?? 0),
      accountName: line.account_name ?? "",
      accountCode: line.account_code ?? "",
      particulars: line.particulars ?? "",
      fromTo: line.from_to ?? null,
      reference: line.reference ?? null,
      in: Number(line.in ?? 0),
      out: Number(line.out ?? 0),
      balance: Number(line.balance ?? 0),
      balanceSide: line.balance_side === "Cr" ? "Cr" : "Dr",
    })),
    totals: {
      in: Number(raw.totals?.in ?? 0),
      out: Number(raw.totals?.out ?? 0),
      balance: Number(raw.totals?.balance ?? 0),
      balanceSide: raw.totals?.balance_side === "Cr" ? "Cr" : "Dr",
    },
  };
}

export function listLeafAccounts(nodes: ChartOfAccountNode[]): ChartOfAccountNode[] {
  return flattenChartTree(nodes).filter(
    (node) => node.level === 3 && node.accountId != null && node.accountId > 0,
  );
}

export function flattenChartTree(nodes: ChartOfAccountNode[]): ChartOfAccountNode[] {
  const rows: ChartOfAccountNode[] = [];
  const walk = (list: ChartOfAccountNode[]) => {
    for (const node of list) {
      rows.push(node);
      if (node.children.length > 0) walk(node.children);
    }
  };
  walk(nodes);
  return rows;
}

export function filterChartTree(
  nodes: ChartOfAccountNode[],
  query: string,
  accountClass: string,
): ChartOfAccountNode[] {
  const q = query.trim().toLowerCase();
  const classFilter = accountClass.trim().toLowerCase();

  const matchesSelf = (node: ChartOfAccountNode): boolean => {
    const classOk = classFilter === "" || classFilter === "all" || node.accountClass.toLowerCase() === classFilter;
    if (!classOk) return false;
    if (q === "") return true;
    return (
      node.name.toLowerCase().includes(q) ||
      node.code.toLowerCase().includes(q) ||
      (node.accountGroup ?? "").toLowerCase().includes(q)
    );
  };

  if (q === "" && (classFilter === "" || classFilter === "all")) {
    return nodes;
  }

  const filterKeepParents = (list: ChartOfAccountNode[]): ChartOfAccountNode[] => {
    const out: ChartOfAccountNode[] = [];
    for (const node of list) {
      const children = filterKeepParents(node.children);
      const selfMatch = matchesSelf(node);
      if (selfMatch) {
        out.push({ ...node, children: node.children });
      } else if (children.length > 0) {
        out.push({ ...node, children });
      }
    }
    return out;
  };

  return filterKeepParents(nodes);
}

export function uniqueAccountClasses(nodes: ChartOfAccountNode[]): string[] {
  const set = new Set<string>();
  for (const row of flattenChartTree(nodes)) {
    if (row.accountClass) set.add(row.accountClass);
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}
